import _ from 'lodash';
import qs from 'query-string';

import { isNilOrEmpty } from './ramdaUtils';
import { POWERBI_ENABLED_APP_SETTING } from '../constants/PowerBIConstants';
import {
  QPS_MANAGE_RIGHT,
  QPS_SUPERVISOR_RIGHT,
  SAVED_SEARCHES_SMARTINSIGHT_TYPE,
} from '../constants/SmartInsightConstants';
import * as Urls from '../constants/Urls';
import {
  CREATE_RIGHT,
  EDIT_RIGHT,
  VIEW_RIGHT,
} from '../entity-designer/constants/entity-designer.constants';
import { FORMS_DESIGN_RIGHT } from '../form-designer/constants/editor.constants';

export const isTrue = (a) => String(a).toLowerCase() === 'true';

export const checkTrue = (a) => {
  const booleanKeys = ['True', 'true', 'Enabled', 'enabled', 'Yes', 'yes'];
  return booleanKeys.includes(a);
};

export const isFalse = (a) => String(a).toLowerCase() === 'false';

export const getHtmlFreeValue = (value) =>
  _.isNil(value) ? '' : value.toString().replace(/(<(\/?[^>]+)>)|&nbsp;/g, '');

export const getUITA = (...theArgs) => theArgs.join(' ');

export const preprocessImportStatuses = (importStatuses) =>
  importStatuses.map((importStatus) => {
    delete importStatus.Data;
    return importStatus;
  });

export function formatString(string, ...args) {
  return string.replace(/{(\d+)}/g, function(match, number) {
    return typeof args[number] != 'undefined' ? args[number] : match;
  });
}

export const getIsAllRecordsPerPageSelected = (allRowsPerPage, selections) =>
  allRowsPerPage.length
    ? allRowsPerPage.every((row) =>
        selections.find(
          (selectedItem) =>
            selectedItem[selectedItem.PrimaryKeyColumn] === row[row.PrimaryKeyColumn]
        )
      )
    : false;

export const getIsSelectedRecordOnCurrentPage = (allRowsPerPage, selections) =>
  allRowsPerPage.some((row) =>
    selections.find(
      (selectedItem) => selectedItem[selectedItem.PrimaryKeyColumn] === row[row.PrimaryKeyColumn]
    )
  );

export const getIsAllRecordsAreDisabled = (allRowsPerPage, getDisableCriteria) =>
  allRowsPerPage.every((row) => getDisableCriteria(row));

export const removeCurrentPageSelection = (allRowsPerPage, selections) =>
  selections.filter(
    (selectionItem) =>
      !allRowsPerPage.find(
        (row) => row[row.PrimaryKeyColumn] === selectionItem[selectionItem.PrimaryKeyColumn]
      )
  );

export const invokeBlobFileDownloading = (data, fileName) => {
  const blobURL = window.URL.createObjectURL(data);
  const tempLink = document.createElement('a');
  tempLink.style.display = 'none';
  tempLink.href = blobURL;
  tempLink.setAttribute('download', fileName);
  tempLink.setAttribute('target', '_blank');
  document.body.appendChild(tempLink);
  tempLink.click();
  document.body.removeChild(tempLink);

  setTimeout(() => {
    window.URL.revokeObjectURL(data);
  }, 100);
};

export const getRepositoryItem = (element, matchingId) => {
  if (element.NodeId === matchingId) {
    return element;
  } else if (element.ChildNodes.length) {
    let i;
    let result = null;
    for (i = 0; result == null && i < element.ChildNodes.length; i++) {
      result = getRepositoryItem(element.ChildNodes[i], matchingId);
    }
    return result;
  }
  return null;
};

export const mapRepositoryDetails = (acc, item) => {
  return {
    ...acc,
    [item.NodeId]: item,
    ...item.ChildNodes.reduce(mapRepositoryDetails, {}),
  };
};

export const isEditorRouteProtected = (userRights, route) =>
  !!userRights.length &&
  !userRights.includes(FORMS_DESIGN_RIGHT) &&
  [Urls.FORM_EDITOR, Urls.FORMS_LIST].includes(route);

export const isEntityRouteProtected = (userRights, route) =>
  !!userRights.length &&
  ![VIEW_RIGHT, EDIT_RIGHT, CREATE_RIGHT].some((item) => userRights.includes(item)) &&
  [Urls.ENTITY_EDITOR, Urls.ENTITIES_LIST, Urls.ENTITY_DETAILS].includes(route);

export const isSmartInsightRouteProtected = (userRights, route, smartInsightType) =>
  !!userRights.length &&
  [Urls.SMART_INSIGHT].includes(route) &&
  (smartInsightType === SAVED_SEARCHES_SMARTINSIGHT_TYPE
    ? !userRights.includes(QPS_SUPERVISOR_RIGHT)
    : !userRights.includes(QPS_MANAGE_RIGHT));

export const isSavedSearchEditorRouteProtected = (userRights, route) =>
  !!userRights.length &&
  !userRights.includes(QPS_SUPERVISOR_RIGHT) &&
  Urls.SAVED_SEARCH_EDITOR === route;

export const isReportEditorRouteProtected = (userRights, route) =>
  !!userRights.length &&
  !userRights.includes(QPS_MANAGE_RIGHT) &&
  [Urls.REPORT_DESIGNER, Urls.REPORT_MODEL_DESIGNER].includes(route);

export const transformFileFormatsToAccept = (files = '') =>
  files
    .split(',')
    .map((type) => `.${type}`)
    .join(',');

export const isFileTypeAllowed = (supportedFileExtensions = '', file) => {
  if (!file) {
    return false;
  }

  const allowedTypes = supportedFileExtensions.split(',');
  const fileType = file.name.split('.').pop();

  return (
    allowedTypes.some((type) => fileType?.toLowerCase() === type.toLowerCase()) &&
    fileType?.toLowerCase() !== 'exe'
  );
};

export const trimObjectStrings = (obj) => {
  if (typeof obj === 'object') {
    if (Array.isArray(obj)) {
      // If it's an array, loop through its elements
      for (let i = 0; i < obj.length; i++) {
        obj[i] = trimObjectStrings(obj[i]);
      }
    } else {
      // If it's an object, loop through its keys
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          obj[key] = trimObjectStrings(obj[key]);
        }
      }
    }
  } else if (typeof obj === 'string') {
    obj = obj.trim();
  }
  return obj;
};

export const isOCRFileTypeAllowed = (supportedOCRFileExtensions = '', extension) => {
  if (!extension) {
    return false;
  }

  return supportedOCRFileExtensions.split(',').includes(extension);
};

export const cleanTextFromHTML = (html = '') => html.replace(/<[^>]*>/g, ' ');

export const includesValueInOptions = (value = '', options = []) => {
  return options.some((ele) => value.toLowerCase().includes(ele.toLowerCase()));
};

export const changeURLParam = ({ paramName, paramValue }) => {
  const url = new URL(window.location);
  url.searchParams.set(paramName, paramValue);
  window.history.pushState(null, '', url.toString());
};

export const isRouteProtected = (location, userRights, appSettings) => {
  const { smartInsightType } = qs.parse(location.search);
  const isFormEditorProtected = isEditorRouteProtected(userRights, location.pathname);
  const isEntityEditorProtected = isEntityRouteProtected(userRights, location.pathname);
  const isSmartInsightEditorProtected = isSmartInsightRouteProtected(
    userRights,
    location.pathname,
    smartInsightType
  );
  const isSavedSearchEditorProtected = isSavedSearchEditorRouteProtected(
    userRights,
    location.pathname
  );
  const isReportEditorProtected = isReportEditorRouteProtected(userRights, location.pathname);

  const isPowerBIProtected =
    !!Object.keys(appSettings).length &&
    !isTrue(appSettings?.[POWERBI_ENABLED_APP_SETTING]) &&
    [Urls.POWERBI_REPORT_PAGE, Urls.POWERBI_REPORTS_PAGE].includes(location.pathname);

  return (
    isFormEditorProtected ||
    isEntityEditorProtected ||
    isSmartInsightEditorProtected ||
    isPowerBIProtected ||
    isSavedSearchEditorProtected ||
    isReportEditorProtected
  );
};

export const modifyBatchPrintClientChanges = (smartSolveDiff, qsParams) => {
  const fields = smartSolveDiff?.[0]?.rows?.[0]?.fields;

  if (Array.isArray(fields)) {
    const { docId, itmId, referenceNumber } = qsParams;

    const parentGtIdField = fields.find((f) => f.columnName === 'PARENT_GT_ID');

    // modify PARENT_GT_ID change
    if (parentGtIdField) {
      parentGtIdField.currentValue = docId;
    }

    // add necessary changes
    fields.push(
      ...[
        {
          columnName: 'DBP_PRINT_RECORD_ID',
          originalValue: null,
          currentValue: docId,
        },
        {
          columnName: 'DBP_DOC_ACO_ID',
          originalValue: null,
          currentValue: itmId,
        },
        {
          columnName: 'DBP_REFERENCE_NUMBER',
          originalValue: null,
          currentValue: ['null', 'undefined'].includes(referenceNumber) ? '' : referenceNumber,
        },
      ]
    );
  }
};

export const decodeString = (xmlStrInput) => {
  if (isNilOrEmpty(xmlStrInput) || typeof xmlStrInput !== 'string') {
    return xmlStrInput;
  }

  const xmlStr = xmlStrInput.toString().replace(/&quot;|&#39;|&nbsp;|&lt;|&gt;|&amp;/g, (x) => {
    const replacementMap = {
      '&quot;': '"',
      '&#39;': "'",
      '&nbsp;': ' ',
      '&lt;': '<',
      '&gt;': '>',
      '&amp;': '&',
    };
    return replacementMap[x] ? replacementMap[x] : x;
  });

  return xmlStr;
};

export const getCustomLogoImageUrl = (svg) => {
  const encodedSvg = encodeURIComponent(svg);
  return `data:image/svg+xml;charset=utf-8,${encodedSvg}`;
};
